import React from 'react';
import { Link } from 'react-router-dom';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { CrowdfundingCampaign } from '../../types/crowdfunding';
import { formatCurrency } from '../../utils/formatters';
// Icônes adaptées
import { Calendar, Users, Palette, User, HeartHandshake } from 'lucide-react';

interface CampaignCardProps {
  campaign: CrowdfundingCampaign;
  className?: string;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, className = '' }) => {
  const progress = campaign.goal_amount > 0 
    ? Math.min(Math.round((campaign.collected_amount / campaign.goal_amount) * 100), 100)
    : 0;
  
  const endDate = new Date(campaign.end_date);
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  
  const isActive = campaign.status === 'active';

  // Détecter si c'est un projet artistique (pas de 'contestant') ou une campagne VotAfrica
  const isArtProject = !campaign.contestant;
  
  return (
    // Style par défaut adapté à Ernest Nzofoug
    <div className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 hover:border-red-500/30 group ${className}`}>
      {/* Image du projet/candidat */}
      <div className="relative h-56 overflow-hidden">
        <Link to={`/crowdfunding/${campaign.slug}`} className="block h-full w-full">
          <img 
            // Utilise l'image du projet si elle existe, sinon celle du candidat, sinon un placeholder
            src={campaign.image_url || campaign.contestant?.photo_url || 'https://placehold.co/600x400/1a1a1a/ffffff?text=Projet'} 
            alt={campaign.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent pointer-events-none"></div>
        
        {/* Statut du projet */}
        {isActive && (
          <div className="absolute top-3 right-3">
            <span className="bg-green-600/80 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm font-semibold">
              En cours
            </span>
          </div>
        )}
        
        {/* Affichage conditionnel : Nom du projet artistique OU Nom du concours */}
        <div className="absolute bottom-3 left-3">
          {isArtProject ? (
            <div className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm text-red-400 text-xs px-2 py-1 rounded-full">
              <Palette className="w-3 h-3" />
              <span>Projet Artistique</span>
            </div>
          ) : (
            // Conserve la logique pour les campagnes VotAfrica
            campaign.contestant?.contest && (
              <Link 
                to={`/c/${campaign.contestant.contest.slug}`}
                className="flex items-center space-x-1 bg-black/50 backdrop-blur-sm text-red-400 text-xs px-2 py-1 rounded-full hover:bg-black/70 transition-colors"
              >
                <Palette className="w-3 h-3" />
                <span>{campaign.contestant.contest.name}</span>
              </Link>
            )
          )}
        </div>
      </div>
      
      <div className="p-5 flex flex-col h-[calc(100%-14rem)]">
        <div className="flex-grow">
          {/* Auteur du projet */}
          <div className="text-sm text-gray-400 mb-2 flex items-center">
            <User className="w-4 h-4 mr-2 text-red-400" />
            <span>Par {isArtProject ? 'Ernest Nzofoug' : campaign.contestant?.name || 'Anonyme'}</span>
          </div>

          <Link to={`/crowdfunding/${campaign.slug}`}>
            <h3 className="text-xl font-bold text-white mb-2 hover:text-red-400 transition-colors line-clamp-2">
              {campaign.title}
            </h3>
          </Link>
          
          <p className="text-gray-400 text-sm mb-4 line-clamp-3">
            {campaign.description}
          </p>
        </div>
        
        {/* Barre de progression */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-white font-semibold">{formatCurrency(campaign.collected_amount)} FCFA</span>
            <span className="text-gray-400">{progress}%</span>
          </div>
          {/* NOTE: Pour que la progress bar soit rouge, il faut peut-être adapter le composant ui/progress ou utiliser une classe comme "accent-red" */}
          <Progress value={progress} className="h-2 [&>div]:bg-red-500" />
          <div className="flex justify-between text-xs mt-1">
            <span className="text-gray-400">Objectif: {formatCurrency(campaign.goal_amount)} FCFA</span>
          </div>
        </div>
        
        {/* Informations supplémentaires */}
        <div className="flex justify-between text-sm text-gray-400 mb-5">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-1.5 text-red-400" />
            <span>{daysLeft} jours restants</span>
          </div>
          <div className="flex items-center">
            <Users className="w-4 h-4 mr-1.5 text-red-400" />
            {(() => {
              const count = Array.isArray(campaign.contributions) && campaign.contributions.length > 0 ? campaign.contributions[0].count : 0;
              return <span>{count} mécène{count > 1 ? 's' : ''}</span>;
            })()}
          </div>
        </div>
        
        {/* Bouton de soutien */}
        <Link to={`/crowdfunding/${campaign.slug}`} className="mt-auto">
          <Button 
            variant="outline" 
            className="w-full bg-red-600/10 border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300 font-bold"
          >
            <HeartHandshake className="w-4 h-4 mr-2" />
            Soutenir ce projet
          </Button>
        </Link>
      </div>
    </div>
  );
};